import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

import Home from "@/pages/Home";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Learn from "@/pages/Learn";
import Library from "@/pages/Library";
import UploadReading from "@/pages/UploadReading";
import Generate from "@/pages/Generate";
import Reading from "@/pages/Reading";
import Progress from "@/pages/Progress";
import ComingSoon from "@/pages/ComingSoon";
import Debate from "@/pages/Debate";
import DebateReport from "@/pages/DebateReport";
import Journal from "@/pages/Journal";
import Research from "@/pages/Research";
import ResearchView from "@/pages/ResearchView";
import WritingMentor from "@/pages/WritingMentor";
import Waitlist from "@/pages/Waitlist";
import Community from "@/pages/Community";
import CommunityVoice from "@/pages/CommunityVoice";
import Unsubscribe from "@/pages/Unsubscribe";
import Admin from "@/pages/Admin";
import TeacherDashboard from "@/pages/TeacherDashboard";
import TeacherClassNew from "@/pages/TeacherClassNew";
import TeacherClassDetail from "@/pages/TeacherClassDetail";
import TeacherStudentDetail from "@/pages/TeacherStudentDetail";
import TeacherDebateReport from "@/pages/TeacherDebateReport";
import JoinClass from "@/pages/JoinClass";
import JoinInstitution from "@/pages/JoinInstitution";
import Onboarding from "@/pages/Onboarding";

function ProtectedRoute({ children, requireAccess = true }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-forge-900 text-forge-100">
        <div className="text-sm tracking-[0.3em] uppercase text-ember-500 animate-pulse">Kindling the forge…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" state={{ from: location }} replace />;
  if (requireAccess && user.access_required && !user.access_granted) {
    return <Navigate to="/onboarding" state={{ from: location }} replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Synchronous OAuth callback detection
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/learn" element={<ProtectedRoute><Learn /></ProtectedRoute>} />
      <Route path="/library" element={<ProtectedRoute><Library /></ProtectedRoute>} />
      <Route path="/upload" element={<ProtectedRoute><UploadReading /></ProtectedRoute>} />
      <Route path="/generate" element={<ProtectedRoute><Generate /></ProtectedRoute>} />
      <Route path="/reading/:id" element={<ProtectedRoute><Reading /></ProtectedRoute>} />
      <Route path="/debate/:debateId" element={<ProtectedRoute><Debate /></ProtectedRoute>} />
      <Route path="/debate/:debateId/report" element={<ProtectedRoute><DebateReport /></ProtectedRoute>} />
      <Route path="/journal" element={<ProtectedRoute><Journal /></ProtectedRoute>} />
      <Route path="/research" element={<ProtectedRoute><Research /></ProtectedRoute>} />
      <Route path="/research/:id" element={<ProtectedRoute><ResearchView /></ProtectedRoute>} />
      <Route path="/writing" element={<ProtectedRoute><WritingMentor /></ProtectedRoute>} />
      <Route path="/writing/:writingId" element={<ProtectedRoute><WritingMentor /></ProtectedRoute>} />
      <Route path="/progress" element={<ProtectedRoute><Progress /></ProtectedRoute>} />
      <Route path="/teacher" element={<ProtectedRoute><TeacherDashboard /></ProtectedRoute>} />
      <Route path="/teacher/classes/new" element={<ProtectedRoute><TeacherClassNew /></ProtectedRoute>} />
      <Route path="/teacher/classes/:classId" element={<ProtectedRoute><TeacherClassDetail /></ProtectedRoute>} />
      <Route path="/teacher/classes/:classId/students/:studentId" element={<ProtectedRoute><TeacherStudentDetail /></ProtectedRoute>} />
      <Route path="/teacher/classes/:classId/students/:studentId/debates/:debateId/report" element={<ProtectedRoute><TeacherDebateReport /></ProtectedRoute>} />
      <Route path="/join-class" element={<ProtectedRoute requireAccess={false}><JoinClass /></ProtectedRoute>} />
      <Route path="/join-institution" element={<ProtectedRoute requireAccess={false}><JoinInstitution /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute requireAccess={false}><Onboarding /></ProtectedRoute>} />
      <Route path="/waitlist" element={<Waitlist />} />
      <Route path="/community" element={<Community />} />
      <Route path="/community-voice" element={<CommunityVoice />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      <Route path="/coming-soon/:slug" element={<ProtectedRoute><ComingSoon /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster theme="dark" position="bottom-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}
